'use client';
import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';

const plans = [
  {
    id: 'decouverte',
    name: 'Découverte',
    price: 'Gratuit',
    priceNum: 0,
    period: '',
    description: 'Commencez à pratiquer sans engagement',
    color: 'bg-white border-border',
    highlight: false,
    features: [
      { text: '10 conversations / mois', included: true },
      { text: 'Correction basique', included: true },
      { text: 'Accès dictionnaire', included: true },
      { text: 'Accès mémo grammaire', included: true },
      { text: 'Correction avancée avec explications', included: false },
      { text: 'Suivi de progression', included: false },
      { text: 'Session avec Marc incluse', included: false },
    ],
    cta: 'Commencer gratuitement',
    ctaLink: '/sign-up-login',
    isPaid: false,
  },
  {
    id: 'immersion',
    name: 'Immersion',
    price: '€19',
    priceNum: 19,
    period: '/mois',
    description: 'La pratique illimitée avec correction avancée',
    color: 'plan-card-popular bg-white',
    highlight: true,
    badge: 'Plus populaire',
    features: [
      { text: 'Conversations illimitées', included: true },
      { text: 'Correction avancée avec explications', included: true },
      { text: 'Accès dictionnaire + audio', included: true },
      { text: 'Mémo grammaire complet', included: true },
      { text: 'Tuteur IA grammaire', included: true },
      { text: 'Suivi de progression', included: true },
      { text: 'Session avec Marc incluse', included: false },
    ],
    cta: 'Choisir Immersion',
    ctaLink: null,
    isPaid: true,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '€39',
    priceNum: 39,
    period: '/mois',
    description: "L\'expérience complète avec Marc en direct",
    color: 'bg-white border-border',
    highlight: false,
    features: [
      { text: 'Tout Immersion inclus', included: true },
      { text: '1 session avec Marc / mois', included: true },
      { text: 'Rapports de progression détaillés', included: true },
      { text: 'Accès prioritaire nouvelles fonctionnalités', included: true },
      { text: 'Support prioritaire', included: true },
      { text: 'Correction avancée avec explications', included: true },
      { text: 'Suivi de progression', included: true },
    ],
    cta: 'Choisir Pro',
    ctaLink: null,
    isPaid: true,
  },
];

export default function AIPlanCards() {
  const [billingAnnual, setBillingAnnual] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const { user } = useAuth();

  const handleStripeCheckout = async (planId: string) => {
    setLoadingPlan(planId);
    setErrorMsg(null);
    try {
      const res = await fetch('/api/stripe/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          planId,
          billingAnnual,
          userEmail: user?.email ?? undefined,
        }),
      });
      const data = await res.json() as { url?: string; error?: string };
      if (!res.ok || data.error) {
        setErrorMsg(data.error ?? 'Une erreur est survenue. Veuillez réessayer.');
        return;
      }
      if (data.url) {
        window.location.href = data.url;
      }
    } catch {
      setErrorMsg('Impossible de se connecter au service de paiement. Veuillez réessayer.');
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        {/* Billing toggle */}
        <div className="flex items-center justify-center gap-3 mb-12">
          <span className={`text-sm font-medium ${!billingAnnual ? 'text-primary' : 'text-muted-foreground'}`}>Mensuel</span>
          <button
            onClick={() => setBillingAnnual(!billingAnnual)}
            className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${billingAnnual ? 'bg-accent' : 'bg-muted'}`}
            aria-label="Basculer facturation annuelle"
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${billingAnnual ? 'translate-x-6' : 'translate-x-0'}`} />
          </button>
          <span className={`text-sm font-medium ${billingAnnual ? 'text-primary' : 'text-muted-foreground'}`}>
            Annuel
            <span className="ml-1.5 text-xs bg-green-100 text-green-700 font-semibold px-2 py-0.5 rounded-full">−20%</span>
          </span>
        </div>

        {errorMsg && (
          <div className="max-w-md mx-auto mb-6 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 text-center">
            {errorMsg}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto">
          {plans?.map((plan) => {
            const displayPrice = billingAnnual && plan?.priceNum > 0
              ? `€${Math.round(plan?.priceNum * 0.8)}`
              : plan?.price;
            const isLoading = loadingPlan === plan.id;

            return (
              <div
                key={`ai-plan-${plan?.id}`}
                className={`relative rounded-2xl border-2 p-7 lg:p-8 flex flex-col ${plan?.color} ${plan?.highlight ? 'shadow-amber scale-[1.03]' : 'shadow-card'}`}
              >
                {plan?.badge && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 amber-gradient rounded-full text-xs font-bold text-primary shadow-sm whitespace-nowrap">
                    ⭐ {plan?.badge}
                  </div>
                )}
                <div className="mb-5">
                  <h3 className="text-xl font-extrabold text-primary mb-1">{plan?.name}</h3>
                  <p className="text-sm text-secondary leading-snug">{plan?.description}</p>
                </div>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-4xl font-extrabold text-primary font-tabular">{displayPrice}</span>
                  {plan?.period && (
                    <span className="text-sm text-muted-foreground">{billingAnnual ? '/mois, facturé annuellement' : plan?.period}</span>
                  )}
                </div>
                <ul className="space-y-2.5 mb-8 flex-1">
                  {plan?.features?.map((feat) => (
                    <li key={`feat-${plan?.id}-${feat?.text?.slice(0, 20)}`} className="flex items-start gap-2.5">
                      <span className={`flex-shrink-0 mt-0.5 w-4 h-4 rounded-full flex items-center justify-center text-xs ${feat?.included ? 'bg-green-100 text-green-600' : 'bg-muted text-muted-foreground'}`}>
                        {feat?.included ? '✓' : '×'}
                      </span>
                      <span className={`text-sm leading-snug ${feat?.included ? 'text-foreground' : 'text-muted-foreground line-through'}`}>
                        {feat?.text}
                      </span>
                    </li>
                  ))}
                </ul>

                {plan.isPaid ? (
                  <button
                    onClick={() => handleStripeCheckout(plan.id)}
                    disabled={isLoading}
                    className={`w-full py-3 rounded-xl text-sm font-semibold text-center transition-all duration-150 flex items-center justify-center gap-2 ${
                      plan?.highlight
                        ? 'btn-primary' :'border-2 border-primary text-primary hover:bg-primary hover:text-white'
                    } ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Redirection…
                      </>
                    ) : (
                      <>
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="1" y="4" width="22" height="16" rx="3" stroke="currentColor" strokeWidth="2"/>
                          <path d="M1 9h22" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        {plan?.cta}
                      </>
                    )}
                  </button>
                ) : (
                  <a
                    href={plan?.ctaLink ?? '/sign-up-login'}
                    className={`w-full py-3 rounded-xl text-sm font-semibold text-center transition-all duration-150 inline-block border-2 border-primary text-primary hover:bg-primary hover:text-white`}
                  >
                    {plan?.cta}
                  </a>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-8">
          Annulez à tout moment • Paiement sécurisé par Stripe • Pas d&apos;engagement
        </p>
      </div>
    </section>
  );
}